import React, { useMemo, useState } from 'react'

import { useQuery } from '@apollo/client/react/hooks/useQuery'

import { GET_CURRENT_CUSTOMER_ORDERS } from 'Const'
import { useFocusReturn } from 'Hooks/useFocusReturn'

import { QueryState } from '../../components/QueryState'

import { OrderCard } from './OrderCard'
import { Order } from './types'
import { groupOrders } from './utils'

const HISTORY_PAGE_SIZE = 5

export const OrdersTab: React.FC = () => {
  const { data, loading, error } = useQuery(GET_CURRENT_CUSTOMER_ORDERS)
  const [historyLimit, setHistoryLimit] = useState(HISTORY_PAGE_SIZE)

  const orders: Order[] = useMemo(() => data?.currentCustomer?.orders ?? [], [data])
  const { active, upcoming, history } = useMemo(() => groupOrders(orders), [orders])
  const visibleHistory = history.slice(0, historyLimit)
  const hasMoreHistory = history.length > historyLimit

  return (
    <div className="user-profile-panel user-profile-panel_enter">
      <QueryState
        emptyLabel="You don't have any orders yet"
        error={error}
        isEmpty={!loading && !error && orders.length === 0}
        loading={loading}
        loadingLabel="Loading your orders…"
      >
        <>
          {active.length > 0 && (
            <section className="user-profile-section">
              <div className="user-profile-section__header">
                <h2 className="user-profile-section__title">Active Orders</h2>
              </div>
              <div className="user-profile-order-list">
                {active.map((order) => (
                  <OrderCard key={order.uuid ?? order.id} order={order} />
                ))}
              </div>
            </section>
          )}

          {upcoming.length > 0 && (
            <section className="user-profile-section">
              <div className="user-profile-section__header">
                <h2 className="user-profile-section__title">Upcoming Orders</h2>
              </div>
              <div className="user-profile-order-list">
                {upcoming.map((order) => (
                  <OrderCard key={order.uuid ?? order.id} order={order} />
                ))}
              </div>
            </section>
          )}

          {history.length > 0 && (
            <HistorySection
              hasMore={hasMoreHistory}
              history={visibleHistory}
              remaining={history.length - historyLimit}
              onLoadMore={() => setHistoryLimit((limit) => limit + HISTORY_PAGE_SIZE)}
            />
          )}
        </>
      </QueryState>
    </div>
  )
}

interface HistorySectionProps {
  hasMore: boolean
  history: Order[]
  onLoadMore: () => void
  remaining: number
}

const HistorySection: React.FC<HistorySectionProps> = ({ hasMore, history, onLoadMore, remaining }) => {
  // Once every order has been revealed, send focus to the section heading
  // instead of letting it fall off the removed "Load more" button.
  const headingRef = useFocusReturn<HTMLHeadingElement>(hasMore)

  return (
    <section className="user-profile-section">
      <div className="user-profile-section__header">
        <h2 ref={headingRef} className="user-profile-section__title" tabIndex={-1}>
          Order History
        </h2>
      </div>
      <div className="user-profile-order-list">
        {history.map((order) => (
          <OrderCard key={order.uuid ?? order.id} order={order} />
        ))}
      </div>
      {hasMore ? (
        <button
          className="user-profile-link-button user-profile-order-list__load-more"
          type="button"
          onClick={onLoadMore}
        >
          Load {Math.min(remaining, HISTORY_PAGE_SIZE)} more
        </button>
      ) : null}
    </section>
  )
}
